'use client'

import * as SheetPrimitive from '@radix-ui/react-dialog'
import { Close } from '@repo/ui/components/close'
import { cn } from '@repo/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { ComponentPropsWithoutRef } from 'react'

function Sheet({ ...props }: ComponentPropsWithoutRef<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot='sheet' {...props} />
}

function SheetTrigger({ ...props }: ComponentPropsWithoutRef<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot='sheet-trigger' {...props} />
}

function SheetClose({ ...props }: ComponentPropsWithoutRef<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot='sheet-close' {...props} />
}

function SheetPortal({ ...props }: ComponentPropsWithoutRef<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot='sheet-portal' {...props} />
}

function SheetOverlay({ className, ...props }: ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot='sheet-overlay'
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60 duration-300',
        className,
      )}
      {...props}
    />
  )
}

const sheetVariants = cva(
  'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col items-stretch overflow-hidden rounded-md transition ease-out',
  {
    variants: {
      side: {
        top: 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-miista-page-padding duration-300 sm:left-1/2 sm:w-[25rem] sm:-translate-x-1/2 lg:bottom-auto',
        bottom:
          'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-miista-page-padding bottom-[var(--spacing-miista-bottom-offset,--spacing-miista-page-padding)] duration-300 sm:left-1/2 sm:w-[25rem] sm:-translate-x-1/2',
        left: 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-x-miista-page-padding bottom-miista-page-padding top-14 min-h-[20rem] duration-300 sm:right-auto sm:w-[25rem] lg:bottom-auto',
        right:
          'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-x-miista-page-padding bottom-miista-page-padding top-14 min-h-[20rem] duration-300 sm:left-auto sm:w-[25rem] lg:bottom-auto',
        all: 'data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-miista-page-padding bottom-miista-page-padding lg:top-miista-page-padding 3xl:h-[80svh] top-14 duration-200 lg:bottom-auto lg:h-[90svh]',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
)

interface SheetContentProps extends ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {}

function SheetContent({ className, children, side = 'all', ...props }: SheetContentProps) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content data-slot='sheet-content' className={cn(sheetVariants({ side }), className)} {...props}>
        {children}
        <SheetPrimitive.Close className='focus-visible:miista-focus absolute left-2 top-2 rounded-sm disabled:pointer-events-none'>
          <Close />
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div data-slot='sheet-header' className={cn('border-border flex flex-col gap-2 border-b border-solid pb-3', className)} {...props} />
}

function SheetFooter({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div data-slot='sheet-footer' className={cn('mb-0 mt-auto flex items-center justify-end gap-2 px-3 pb-3 pt-2', className)} {...props} />
}

function SheetTitle({ className, ...props }: ComponentPropsWithoutRef<typeof SheetPrimitive.Title>) {
  return <SheetPrimitive.Title data-slot='sheet-title' className={cn('text-foreground space-x-2 px-3 py-2 text-right uppercase', className)} {...props} />
}

function SheetDescription({ className, ...props }: ComponentPropsWithoutRef<typeof SheetPrimitive.Description>) {
  return <SheetPrimitive.Description data-slot='sheet-description' className={cn('p-3', className)} {...props} />
}

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger }
